/**
 * Color utilities for analytics charts
 * V3 Design System - Unified data visualization palette
 */

import AppStyles from '../../AppStyles';
import { t } from '../../i18n';

const HRO_CODE_LIST = ['PF', 'RS', 'SO', 'CR', 'DE'];

// V3 Categorical palette - harmonious colors for data visualization
export const CATEGORICAL_PALETTE = [
  AppStyles.colorSet.chartBlue,    // #3b82f6
  AppStyles.colorSet.chartAmber,   // #f59e0b
  AppStyles.colorSet.chartGreen,   // #22c55e
  AppStyles.colorSet.chartPurple,  // #8b5cf6
  AppStyles.colorSet.chartRed,     // #ef4444
  AppStyles.colorSet.chartTeal,    // #14b8a6
  AppStyles.colorSet.chartIndigo,  // #6366f1
  AppStyles.colorSet.chartPink,    // #ec4899
  AppStyles.colorSet.chartGray,    // #6b7280
  // Extended palette for many categories
  '#0ea5e9', // sky
  '#84cc16', // lime
  '#a855f7', // purple variant
  '#f43f5e', // rose
  '#06b6d4', // cyan
  '#eab308', // yellow
  '#d946ef', // fuchsia
];

// HRO category colors - V3 consistent palette
export const HRO_COLORS = {
  PF: AppStyles.colorSet.chartGray,    // Neutral - baseline monitoring
  RS: AppStyles.colorSet.chartAmber,   // Warning - requires attention
  SO: AppStyles.colorSet.chartBlue,    // Primary - operational focus
  CR: AppStyles.colorSet.chartPurple,  // Distinct - resilience commitment
  DE: AppStyles.colorSet.chartRed,     // Urgent - expertise needed
};

/**
 * Abbreviated HRO labels for chart legends, in the active language.
 *
 * A function rather than a constant: the labels are translated, so they have to
 * be resolved at call time instead of frozen at module load.
 *
 * @returns {Object<string, string>} code -> abbreviated label
 */
export function getHroLabels() {
  return HRO_CODE_LIST.reduce((labels, code) => {
    labels[code] = t(`reports:hro.short.${code}`);
    return labels;
  }, {});
}

// Graph line colors - V3 consistent with semantic meaning
export const GRAPH_COLORS = {
  GRAY: AppStyles.colorSet.chartGray,
  AMBER: AppStyles.colorSet.chartAmber,
  BLUE: AppStyles.colorSet.chartBlue,
  PURPLE: AppStyles.colorSet.chartPurple,
  RED: AppStyles.colorSet.chartRed,
  // Legacy alias
  ORANGE: AppStyles.colorSet.chartAmber,
};

/**
 * Generate a random RGB color
 * @returns {string}
 */
export function getRandomColor() {
  return `rgb(${Math.floor(Math.random() * 256)}, ${Math.floor(Math.random() * 256)}, ${Math.floor(Math.random() * 256)})`;
}

/**
 * Generate a set of unique random colors
 * @param {number} totalColors
 * @returns {Set<string>}
 */
export function getRandomColors(totalColors) {
  const colors = new Set();
  while (colors.size < totalColors) {
    colors.add(getRandomColor());
  }
  return colors;
}

/**
 * Get color from categorical palette by index (wraps around)
 * @param {number} index
 * @returns {string}
 */
export function getCategoricalColor(index) {
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}
